(ns common.resource.auth
  (:require [clojure.string :as str]
            [common.db.effects.http :as http]
            [common.os :as os]
            [common.url-template :as url-tpl]
            [medley.core :as medley]
            [portal.os-access-modal.request-demo-account :refer [request-demo-account-controls]]
            [re-frame.core :as rf]
            [uix.core :refer [$]]))

(def login-redirect-url-localstorage-key "login-redirect-url")

(rf/reg-event-fx :resource.auth/change-password
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context] :as cofx} [_ payload]]
    {:http
     {:method :post
      :uri (url-tpl/render :api/change-password url-context)
      :params payload
      :headers {:Authorization (:resource.user/token cofx)}
      :on-success [:resource.auth/change-password-success]
      :on-failure [:resource.auth/change-password-failure]}}))

(rf/reg-event-db :resource.auth/change-password-success
  (fn [db _]
    db))

(rf/reg-event-db :resource.auth/change-password-failure
  (fn [db _]
    db))

(rf/reg-event-fx :resource.auth/unsign-preview-token
  [(rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context]} [_ payload]]
    {:http
     {:method :post
      :uri (url-tpl/render :api/unsign-token url-context)
      :params payload
      :on-success [:resource.auth/unsign-preview-token-success]
      :on-failure [:resource.auth/unsign-preview-token-failure]}}))

(rf/reg-event-db :resource.auth/unsign-preview-token-success
  (fn [db _]
    db))

(rf/reg-event-db :resource.auth/unsign-preview-token-failure
  (fn [db _]
    db))

(rf/reg-event-fx :resource.auth/switch-agency
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [url-context] :as cofx} [_ agency-id]]
    {:fx [[:http {:method :post
                  :uri (url-tpl/render :api/switch-agency url-context)
                  :params {:agency-id agency-id}
                  :headers {"Authorization" (:resource.user/token cofx)}
                  :on-success [:resource.auth/switch-agency-success agency-id]
                  :on-failure [:resource.auth/switch-agency-failure]}]]}))

(def switch-agency-flow
  {:id :resource.auth/switch-agency-flow
   :rules [{:when :seen?
            :events :resource.user/get-me-success
            :halt? true
            :dispatch [:resource.auth/switched-agency]}
           {:when :seen?
            :events :resource.user/get-me-failure
            :halt? true
            :dispatch [:resource.auth/switch-agency-failure]}]})

(rf/reg-event-fx :resource.auth/switch-agency-success
  (fn [{:keys [db]} [_ agency-id {:keys [auth-token refresh-token]}]]
    {:db (update db :resource.user/me merge {:agencyId agency-id
                                             :token auth-token
                                             :refresh-token refresh-token})
     :fx [[:qhttp/set-auth-token! auth-token]
          [:async-flow switch-agency-flow]
          [:dispatch [:resource.user/get-me]]
          [:invalidate-queries {:queryKey ["user"]}]]}))

(rf/reg-event-fx :resource.auth/switched-agency
  [(rf/inject-cofx :sub :resource.user/me)]
  (fn [{{:keys [role agencyId token refresh-token] :as user-data} :resource.user/me} _]
    (let [user (select-keys user-data [:role :email :updatedAt :privacySettingsAccepted
                                       :name :createdAt :id :agencyId])
          value {:user user
                 :role role
                 :agencyId agencyId}
          session {:agencyId agencyId
                   :refreshToken refresh-token
                   :token token}]
      {:fx [[:sentry/user value]
            [:localstorage/save ["session" session]]
            [:invalidate-queries {:queryKey ["advertisers"]}]]})))

(rf/reg-event-db :resource.auth/switch-agency-failure
  (fn [db _] db))

(defn refresh-token-fx [{:keys [url-context refresh-token sso-token]}]
  {:method :post
   :uri (url-tpl/render
         :api/refresh-token url-context)
   :params (->> {:refresh-token refresh-token
                 :sso-token sso-token}
                (medley/remove-vals nil?))
   :on-success [:resource.auth/refresh-token-success]
   :on-failure [:resource.auth/refresh-token-failure]})

(rf/reg-event-fx :resource.auth/refresh-token
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/refresh-token)]
  (fn [{:keys [url-context] :resource.user/keys [refresh-token]} _]
    (if (os/os-environment?)
      ;; for OS we need to get recent OS token first
      {:fx [[::refresh-token-os {:url-context url-context
                                 :refresh-token refresh-token}]]}
      {:fx [[:http (refresh-token-fx {:url-context url-context
                                      :refresh-token refresh-token})]]})))

(rf/reg-fx ::refresh-token-os
  (fn [{:keys [_url-context _refresh-token] :as params}]
    (when os/*penpal*
      (-> (.-promise os/*penpal*)
          (.then (fn [^js r]
                   (.getAccessToken (.-osApi r))))
          (.then (fn [token]
                   (os/set-token token)
                   (http/http-fx (refresh-token-fx (assoc params :sso-token token)))))
          (.catch (fn [_e]
                    ;; No connection to OS parent frame,
                    ;; thrown if no reply without timeout.
                    ))))))

(def refresh-token-flow
  {:id :resource.auth/refresh-token-flow
   :first-dispatch [:resource.user/get-me]
   :rules [{:when :seen?
            :events :resource.user/get-me-success
            :halt? true
            :dispatch [:resource.auth/refresh-success]}
           {:when :seen?
            :events :resource.user/get-me-failure
            :halt? true
            :dispatch [:resource.auth/refresh-token-failure]}]})

(rf/reg-event-fx :resource.auth/refresh-token-success
  [(rf/inject-cofx :localstorage ["session"])]
  (fn [{:keys [db]
        :localstorage/keys [session]} [_ {:keys [auth-token docs-site-password]}]]
    {:db (-> (assoc-in db [:resource.user/me :token] auth-token)
             (assoc-in [:resource.auth/auth :docs-site-password] docs-site-password))
     :fx [[:qhttp/set-auth-token! auth-token]
          [:localstorage/save ["session" (assoc session :token auth-token)]]
          [:async-flow refresh-token-flow]]}))

(rf/reg-event-fx :resource.auth/refresh-token-failure
  (fn [{:keys [db]} _]
    {:db (assoc-in db [:resource.auth/auth :status] :failure)
     :fx [[:localstorage/remove ["session"]]
          [:dispatch-interval {:unregister ::refresh-token}]]}))

(rf/reg-event-fx :resource.auth/refresh-success
  (fn [{:keys [db]} _]
    {:db (assoc-in db [:resource.auth/auth :status] :success)}))

(rf/reg-event-fx :resource.auth/init-refresh-token-timer
  (fn [_ _]
    {:dispatch-interval {:register ::refresh-token
                         :event [:resource.auth/refresh-token]
                         :ms (* 1000 60 15)}}))

(rf/reg-event-fx :resource.auth/init
  [(rf/inject-cofx :localstorage ["session"])]
  (fn [{:keys [db]
        :localstorage/keys [session]}]
    {:db (-> db
             (update :resource.user/me merge {:refresh-token (:refreshToken session)
                                              :agencyId (:agencyId session)})
             (assoc-in [:resource.auth/auth :status] :initializing))

     :fx [(if (:refreshToken session)
            [:dispatch [:resource.auth/init-refresh-token-timer]]
            [:dispatch [:resource.auth/init-failure]])]}))

(rf/reg-event-db :resource.auth/init-failure
  (fn [db _]
    (assoc-in db [:resource.auth/superadmin-auth :status] :failure)))

(rf/reg-event-fx :resource.auth/login
  (fn [{:keys [db]} [_ {:keys [data on-failure]}]]
    {:db (assoc-in db [:resource.auth/auth :status] :log-in)
     :fx [[:async-flow {:id :resource.auth/login-flow
                        :first-dispatch [:resource.user/get-token data]
                        :rules [{:when :seen?
                                 :events :resource.user/merge-value
                                 :dispatch [:resource.user/get-me]}
                                {:when :seen-any-of?
                                 :events [:resource.user/get-token-failure
                                          :resource.user/get-me-failure]
                                 :halt? true
                                 :dispatch-n (cond-> [[:resource.auth/login-failure]]
                                               on-failure (conj on-failure))}
                                {:when :seen?
                                 :events :resource.user/get-me-success
                                 :halt? true
                                 :dispatch [:resource.auth/login-success]}]}]]}))

(defn- navigate-for-wpp-open [context]
  (cond
    ;; Avoid extra redirects after login flow is done if inside OS
    (:campaign-id context)
    [:router/navigate! [:campaign/page {:params context}]]

    (:advertiser-id context)
    [:router/navigate! [:advertiser/page {:params context}]]

    ;; Agency without advertiser - show demo account screen
    (:agency-id context)
    [:dispatch [:router/update-controller-status
                {:failure? true
                 :failure-props {:code "os_workspace_mapping_missing_demo_available"
                                 :appbar false
                                 :sidebar false
                                 :buttons ($ request-demo-account-controls)}}]]

    :else
    [:dispatch [::os/resolve-os-context-failure]]))

(rf/reg-event-fx :resource.auth/login-success
  [(rf/inject-cofx :sub :resource.user/me)
   (rf/inject-cofx :sub :resource.user/has-one-role?)
   (rf/inject-cofx :sub :common.os/enabled)
   (rf/inject-cofx :localstorage [login-redirect-url-localstorage-key "default-agency" "last-viewed-advertiser-id"])]
  (fn [{:keys [db resource.user/has-one-role?]
        :localstorage/keys [login-redirect-url default-agency last-viewed-advertiser-id]
        {:keys [role roles agencyId token refresh-token] :as user-data} :resource.user/me
        os-enabled :common.os/enabled} _]
    (let [user (select-keys user-data [:role :email :updatedAt :privacySettingsAccepted
                                       :name :createdAt :id :agencyId :ssoEnabled])
          value {:user user
                 :role role
                 :agencyId agencyId}
          session {:agencyId agencyId
                   :refreshToken refresh-token
                   :token token}
          superadmin-route (str/includes? (or login-redirect-url "") "/superadmin")]
      {:db (assoc-in db [:resource.auth/auth :status] :success)
       :fx [[:sentry/user value]
            [:localstorage/remove [login-redirect-url-localstorage-key]]
            [:localstorage/save ["session" session]]
            [:dispatch-later {:ms 1000 :dispatch [:resource.auth/init-refresh-token-timer]}]
            [:dispatch [:ga4/track-user-login (select-keys user [:email :ssoEnabled :roles])]]
            (cond
              ;; Avoid extra redirects after login flow is done if inside OS
              ;; for the case of superadmin app it just skipped and navigating to login-redirect-url
              (and os-enabled (not superadmin-route))
              (navigate-for-wpp-open (:context db))

              login-redirect-url
              [:router/navigate! login-redirect-url]

              (empty? roles)
              [:router/navigate! :common/user]

              has-one-role?
              [:dispatch [:router/navigate-home]]

              ;; directly navigate to default agency (set in cookies), but only if
              ;; the specified agency is valid (user has access)
              (and (some? default-agency)
                   (some #(= (:agencyId %) default-agency) roles)
                   (some? last-viewed-advertiser-id))
              [:router/navigate! [:advertiser/page {:params {:agency-id default-agency
                                                             :advertiser-id last-viewed-advertiser-id}}]]

              :else
              [:router/navigate! [:common/agency-selection]])]})))

(rf/reg-event-fx :resource.auth/login-failure
  (fn [{:keys [db]}]
    {:db (assoc-in db [:resource.auth/auth :status] :failure)
     :fx [[:localstorage/remove [login-redirect-url-localstorage-key]]
          [:localstorage/remove ["session"]]]}))

(rf/reg-event-fx :resource.auth/logout
  (fn [{:keys [db]} [_ {:keys [href]}]]
    {:db (-> db
             (dissoc :resource.user/me)
             (assoc-in [:resource.auth/auth :status] :log-in))
     :fx [[:qhttp/set-auth-token! nil]
          [:localstorage/remove ["session"]]
          [:dispatch [:modal.privacy/init]]
          [:router/navigate! (or href :common/login)]]}))

(rf/reg-sub :resource.auth/auth
  (fn [db]
    (get db :resource.auth/auth)))

(rf/reg-sub :resource.auth/init-status
  (fn [db] (get-in db [:resource.auth/auth :status])))
